import * as types from './types';

const initialState = {};

export default (state = initialState, action) => {
  switch (action.type) {
    case types.ADD_LEAD: {
      const id = action.payload?.id;
      const data = action.payload?.data || {};
      if (!id) return state;

      const prev = state[id] || {};

      const merged = Object.entries(data).reduce((acc, [key, value]) => {
        if (acc[key] === undefined) {
          acc[key] = value;
        }
        return { ...acc, [key]: value };
      }, { ...prev });

      return {
        ...state,
        [id]: merged,
      };
    }
    case types.DELETE_LEAD_SUCCESS: {
      if (!action.payload?.id) return state;

      const newState = { ...state };
      delete newState[action.payload.id];

      return newState;
    }
    case types.ADD_LEAD_COMMENTS: {
      if (!action.payload?.id) return state;
      const lead = state[action.payload.id];
      const newLead = { ...lead, comments: action.payload.data };
      return { ...state, [action.payload.id]: newLead };
    }

    case types.UPDATE_CHECKLIST_ITEM_STORE:
    case types.REMOVE_CHECKLIST_ITEM_STORE:
    case types.UPDATE_CHECKLIST_ASSIGNMENT_STORE:
    case types.REMOVE_CHECKLIST_ASSIGNMENT_STORE:
    case types.UPDATE_CHECKLIST_COMMENT_STORE:
    case types.REMOVE_CHECKLIST_COMMENT_STORE:
    case types.RESORT_CHECKLIST_ITEMS_STORE:
    case types.SET_CHECKLIST_REMINDERS:
      return reduceLeadChecklist(state, action);

    default:
      return state;
  }
};

const reduceLeadChecklist = (state, action) => {
  const { id: leadId } = action.payload || {};
  if (!leadId) return state;

  const lead = state[leadId] || {};
  const prev = lead.checklists || {};
  let newLeadData = { checklists: { ...prev } };

  switch (action.type) {
    case types.UPDATE_CHECKLIST_ITEM_STORE:
    case types.REMOVE_CHECKLIST_ITEM_STORE:
    case types.RESORT_CHECKLIST_ITEMS_STORE: {
      const nextChecklistsData = reduceChecklistItems(prev, action);
      if (nextChecklistsData === prev) return state;
      newLeadData = { checklists: nextChecklistsData };
      break;
    }
    case types.UPDATE_CHECKLIST_ASSIGNMENT_STORE:
    case types.REMOVE_CHECKLIST_ASSIGNMENT_STORE: {
      const nextAssignmentsData = reduceLeadChecklistAssignments(
        prev.assignments || {},
        action,
      );
      if (!nextAssignmentsData) return state;
      newLeadData.checklists = {
        ...prev,
        assignments: nextAssignmentsData.assignments ?? prev.assignments,
        assignmentCount:
          nextAssignmentsData.assignmentCount ?? prev.assignmentCount,
      };
      break;
    }
    case types.UPDATE_CHECKLIST_COMMENT_STORE:
    case types.REMOVE_CHECKLIST_COMMENT_STORE: {
      const nextCommentsData = reduceLeadChecklistComments(
        prev.comments || [],
        prev.commentCounts || {},
        action,
      );
      if (!nextCommentsData) return state;

      newLeadData.checklists = {
        ...prev,
        comments: nextCommentsData.comments ?? prev.comments,
        commentCounts: nextCommentsData.commentCounts ?? prev.commentCounts,
      };
      break;
    }
    case types.SET_CHECKLIST_REMINDERS: {
      const nextRemindersData = reduceLeadChecklistReminders(
        prev.reminders || {},
        action,
      );
      if (!nextRemindersData) return state;

      newLeadData.checklists = {
        ...prev,
        reminders: nextRemindersData.reminders ?? prev.reminders,
      };
      break;
    }
    default:
      return state;
  }

  const newLead = { ...lead, ...newLeadData };
  if (newLead === lead) return state;

  return { ...state, [leadId]: newLead };
};

const safeMergeChecklists = (prev = {}, patch = {}) => {
  return {
    ...prev,
    ...patch,
    assignmentCount: patch.assignmentCount ?? prev.assignmentCount ?? 0,
    assignments: patch.assignments ?? prev.assignments ?? {},
    commentCounts: patch.commentCounts ?? prev.commentCounts ?? {},
    comments: patch.comments ?? prev.comments ?? [],
    items: patch.items ?? prev.items ?? [],
    uncategorized: patch.uncategorized ?? prev.uncategorized ?? [],
    reminders: patch.reminders ?? prev.reminders ?? {},
  };
};

const reduceChecklistItems = (checklists = {}, action) => {
  const { data, op } = action.payload || {};

  if (!op && data) return safeMergeChecklists(checklists, data);

  switch (op) {
    case 'LOAD': {
      const payloadData = {
        items: data.items || [],
        assignmentCount: data.assignmentCount ?? 0,
        assignments: data.assignments ?? {},
        uncategorized: data.uncategorized ?? [],
        commentCounts: data.commentCounts ?? {},
        reminders: data.reminders ?? {},
      };
      return safeMergeChecklists(checklists, payloadData);
    }

    case 'CREATE_SUCCESS': {
      const { item, changedItems } = action.payload || {};
      if (!item) return checklists;

      const prevItems = checklists.items || [];

      const items = [...prevItems, item].map((it) => {
        const { id } = it;
        const found = changedItems.find((changedItem) => changedItem.taskId === id);
        if (found) return { ...it, ...found };
        return it;
      });

      items.sort((a, b) => a.uncategorizedOrder - b.uncategorizedOrder);

      const uncategorized = items.map(_item => (
        { taskId: _item.id, uncategorizedOrder: _item.uncategorizedOrder }
      ));

      return safeMergeChecklists(checklists, { items, uncategorized });
    }

    case 'UPDATE': {
      const { checklistItemId, updateData } = action.payload || {};
      if (!checklistItemId || !updateData) return checklists;

      const items = (checklists.items || []).map((it) =>
        it.id === checklistItemId ? { ...it, ...updateData } : it,
      );
      return safeMergeChecklists(checklists, { items });
    }

    case 'UPDATE_SUCCESS': {
      const { checklistItemId, item } = action.payload || {};
      if (!item || !checklistItemId) return checklists;

      const items = (checklists.items || []).map((it) =>
        it.id === checklistItemId ? { ...it, ...item } : it,
      );
      return safeMergeChecklists(checklists, { items });
    }

    case 'DELETE': {
      const { checklistItemId } = action.payload || {};
      if (!checklistItemId) return checklists;

      const item = (checklists.items || []).find(
        (it) => it.id === checklistItemId,
      );
      const discussionId = item?.discussionId;

      const items = (checklists.items || []).filter(
        (it) => it.id !== checklistItemId,
      );
      const uncategorized = (checklists.uncategorized || []).filter(
        (r) => r.taskId !== checklistItemId,
      );

      const { [checklistItemId]: _drop, ...commentCounts } =
        checklists.commentCounts || {};

      const assignments = Object.entries(checklists.assignments || {}).reduce(
        (acc, [loginId, arr]) => {
          const filtered = (arr || []).filter(
            (a) => a.taskId !== checklistItemId,
          );
          if (filtered.length > 0) acc[loginId] = filtered;
          return acc;
        },
        {},
      );
      const assignmentCount = Object.values(assignments).reduce(
        (sum, arr) => sum + (arr?.length || 0),
        0,
      );

      const comments = (checklists.comments?.[checklistItemId] || []).filter((c) =>
        discussionId == null ? true : c.discussionId !== discussionId,
      );

      return safeMergeChecklists(checklists, {
        items,
        uncategorized,
        commentCounts,
        assignmentCount,
        assignments,
        comments,
      });
    }

    case 'REVERT': {
      const { snapshotChecklists } = action.payload || {};
      if (!snapshotChecklists) return checklists;
      return safeMergeChecklists(checklists, snapshotChecklists);
    }

    case 'RESORT': {
      const { items = [] } = action.payload || [];

      const uncategorized = items.map(it => ({
        taskId: it.id ?? it.taskId,
        uncategorizedOrder: it.uncategorizedOrder,
      }));

      return safeMergeChecklists(checklists, { items, uncategorized });
    }

    default:
      return checklists;
  }
};

const reduceLeadChecklistAssignments = ( assignments = {}, action) => {
  const { op } = action.payload || {};

  switch (op) {
    case 'CREATE': {
      const { loginId, checklistItemId } = action.payload || {};
      if (!loginId || !checklistItemId) return null;

      const arr = assignments[loginId] || [];
      if (arr.some((a) => a.taskId === checklistItemId)) {
        return {
          assignments,
          assignmentCount: Object.values(assignments).reduce(
            (s, a) => s + (a?.length || 0),
            0,
          ),
        };
      }

      const temp = {
        taskId: checklistItemId,
        loginId,
        outId: null,
        focusLevel: 2,
        loginOrder: null,
        focusLoginOrder: 0,
      };

      const nextAssignments = { ...assignments, [loginId]: [...arr, temp] };
      const assignmentCount = Object.values(nextAssignments).reduce(
        (s, a) => s + (a?.length || 0),
        0,
      );
      return { assignments: nextAssignments, assignmentCount };
    }

    case 'CREATE_SUCCESS': {
      const { loginId, assignment } = action.payload || {};
      if (!loginId || !assignment) return null;

      const updated = (assignments[loginId] || []).map((a) =>
        a.taskId === assignment.taskId ? { ...a, ...assignment } : a,
      );
      const nextAssignments = { ...assignments, [loginId]: updated };
      const assignmentCount = Object.values(nextAssignments).reduce(
        (s, a) => s + (a?.length || 0),
        0,
      );
      return { assignments: nextAssignments, assignmentCount };
    }

    case 'DELETE': {
      const { loginId, checklistItemId } = action.payload || {};
      if (!loginId || !checklistItemId) return null;

      const arr = assignments[loginId] || [];
      const filtered = arr.filter((a) => a.taskId !== checklistItemId);

      const nextAssignments =
        filtered.length > 0
          ? { ...assignments, [loginId]: filtered }
          : (() => {
              const { [loginId]: _drop, ...rest } = assignments;
              return rest;
            })();

      const assignmentCount = Object.values(nextAssignments).reduce(
        (s, a) => s + (a?.length || 0),
        0,
      );
      return { assignments: nextAssignments, assignmentCount };
    }

    case 'REVERT': {
      const { snapshotChecklists } = action.payload || {};
      if (!snapshotChecklists) return null;

      return {
        assignments: snapshotChecklists.assignments,
        assignmentCount: snapshotChecklists.assignmentCount,
      };
    }

    default:
      return null;
  }
};

const reduceLeadChecklistComments = (
  comments = {},
  commentCounts = {},
  action,
) => {
  const { op, data, checklistItemId } = action.payload || {};

  switch (op) {
    case 'LOAD': {
      const nextComments = {
        ...comments,
        [checklistItemId]: data ?? [],
      };
      return { comments: nextComments, commentCounts };
    }

    case 'CREATE': {
      const { tempId, tempDate, comment, discussionId } =
        action.payload || {};
      if (!checklistItemId || !tempId || !tempDate || !comment || !discussionId)
        return null;

      const tempComment = {
        id: tempId,
        discussionId,
        loginId: null,
        clientEmailId: null,
        clientEmail: null,
        createdAt: tempDate,
        updatedAt: null,
        kind: 'user',
        body: comment,
        firstname: null,
        lastname: null,
        email: null,
        html: comment,
        login: {
          id: null,
          firstname: '',
          lastname: '',
          username: '',
          initials: '',
          profileImage: null,
        },
      };

      const nextComments = {
        ...comments,
        [checklistItemId]: [...comments[checklistItemId] ?? [], tempComment],
      };
      const nextCounts = {
        ...commentCounts,
        [checklistItemId]: (commentCounts[checklistItemId] ?? 0) + 1,
      };

      return { comments: nextComments, commentCounts: nextCounts };
    }

    case 'CREATE_SUCCESS': {
      const { tempId, comment } = action.payload || {};
      if (!tempId || !comment || !comments[checklistItemId]) return null;

      const nextComments = {
        ...comments,
        [checklistItemId]: [
          ...comments[checklistItemId].filter((c) => c.id !== tempId),
          comment,
        ],
      };

      return { comments: nextComments, commentCounts };
    }

    case 'UPDATE': {
      const { commentId, comment, updatedAt } = action.payload || {};
      if (!commentId || comment == null || !comments[checklistItemId]) return null;

      const nextComments = comments[checklistItemId].map((c) =>
        c.id === commentId ? {
          ...c,
          body: comment,
          html: comment,
          updatedAt,
        } : c,
      );
      return {
        comments: {
          ...comments,
          [checklistItemId]: nextComments,
          commentCounts,
        },
      };
    }

    case 'DELETE': {
      const { commentId } = action.payload || {};
      if (!checklistItemId || !commentId || !comments[checklistItemId]) return null;

      const nextComments = comments[checklistItemId].filter((c) => c.id !== commentId);
      const nextCounts = { ...commentCounts };

      if (nextCounts[checklistItemId] > 1) {
        nextCounts[checklistItemId] -= 1;
      } else {
        delete nextCounts[checklistItemId];
      }
      return {
        comments: {
          ...comments,
          [checklistItemId]: nextComments,
          commentCounts: nextCounts,
        },
      };
    }
    case 'REVERT': {
      const { snapshotChecklists } = action.payload || {};
      if (!snapshotChecklists) return null;

      return {
        comments: snapshotChecklists.comments,
        commentCounts: snapshotChecklists.commentCounts,
      };
    }

    default:
      return null;
  }
};

const reduceLeadChecklistReminders = (reminders = {}, action) => {
  switch (action.type) {
    case types.SET_CHECKLIST_REMINDERS: {
      const { taskId, reminders: taskReminders } = action.payload || {};
      if (!taskId) return null;

      return { reminders: { ...reminders, [taskId]: taskReminders || [] } };
    }

    default:
      return null;
  }
};
